import type { CrudFilters } from '@refinedev/core';
import type {
  Severity,
  SummaryBarQuery,
} from '@/components/common/SummaryAlertsBar';
import { dayRangeFilters } from '@/utils/dateRange';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Needed for the strict parsing in `parseDateSelection`; `extend` is
// idempotent, so this is safe next to the app-wide plugins in main.tsx.
dayjs.extend(customParseFormat);

/** Date dimensions the API can range-filter. */
export type DateDimension = 'created' | 'starting' | 'ending' | 'updated';
export type Window = 'today' | '3d' | '7d' | 'since_saturday';
/** "Created in…", "Starting in…": the user then picks the window. */
export type DateViewKey = `${DateDimension}_in`;

export type SummaryKey =
  | 'all'
  | 'available'
  | 'upcoming'
  | 'expired'
  | 'not_imported'
  | 'expired_enabled'
  | 'licensed_disabled'
  | 'no_contract'
  | 'ending_3d'
  | 'active_today'
  | 'starting_today'
  | DateViewKey;

export type { Severity };

/** Alerts carry a severity; plain stats do not. */
export type SummaryQuery = SummaryBarQuery<SummaryKey>;

/** What a date view is scoped to: a preset window or an explicit range. */
export type DateSelection =
  | { kind: 'window'; window: Window }
  | { kind: 'range'; from: string; to: string };

export const DEFAULT_DATE_SELECTION: DateSelection = {
  kind: 'window',
  window: 'today',
};

const DAY = 'YYYY-MM-DD';

const IMPORTED: CrudFilters = [
  { field: 'user_catalog_publication_id', operator: 'nnull', value: true },
];

/**
 * Whether the period can be consumed right now — the backend's verdict
 * (window, contract, …), so "licensed" questions read this rather than the
 * status alone. The contract alert is the exception: it is about the window.
 */
const distributable = (value: boolean): CrudFilters => [
  { field: 'is_distributable', operator: 'eq', value },
];

const FIELD_BY_DIMENSION: Record<DateDimension, string> = {
  created: 'created_at',
  starting: 'started_at',
  ending: 'ended_at',
  updated: 'updated_at',
};

export const DATE_DIMENSIONS: DateDimension[] = [
  'created',
  'starting',
  'ending',
  'updated',
];
export const WINDOWS: Window[] = ['today', '3d', '7d', 'since_saturday'];

export const isDateView = (key: SummaryKey | null): key is DateViewKey =>
  key != null && DATE_DIMENSIONS.some((d) => key === `${d}_in`);

export const dimensionOf = (key: DateViewKey): DateDimension =>
  key.replace(/_in$/, '') as DateDimension;

/**
 * The most recent Saturday, today included — the Monday review covers the
 * weekend, and on Saturday itself the window starts today.
 */
export const lastSaturday = (today: dayjs.Dayjs): dayjs.Dayjs =>
  today.subtract((today.day() + 1) % 7, 'day');

const between = (field: string, from: string, to: string): CrudFilters =>
  dayRangeFilters(field, from, to);

/**
 * Starting / ending look forward (today … today + N); created / updated
 * look back (today − N … today). "Since Saturday" is always the past window.
 */
export const dateWindow = (
  dimension: DateDimension,
  window: Window,
  today: dayjs.Dayjs
): CrudFilters => {
  const field = FIELD_BY_DIMENSION[dimension];
  const forward = dimension === 'starting' || dimension === 'ending';
  const d = (v: dayjs.Dayjs) => v.format(DAY);
  switch (window) {
    case 'today':
      return between(field, d(today), d(today));
    case '3d':
    case '7d': {
      const n = window === '3d' ? 3 : 7;
      return forward
        ? between(field, d(today), d(today.add(n, 'day')))
        : between(field, d(today.subtract(n, 'day')), d(today));
    }
    case 'since_saturday':
      return between(field, d(lastSaturday(today)), d(today));
  }
};

/** Filters for a date view under the user's current selection. */
export const dateFilters = (
  dimension: DateDimension,
  selection: DateSelection,
  today = dayjs()
): CrudFilters =>
  selection.kind === 'window'
    ? dateWindow(dimension, selection.window, today)
    : between(FIELD_BY_DIMENSION[dimension], selection.from, selection.to);

/**
 * Filter sets behind the alert bar, derived from the list endpoint
 * (no dedicated stats endpoint). `today` is injected for testability.
 * Order is display order.
 */
export const summaryQueries = (today = dayjs()): SummaryQuery[] => [
  {
    key: 'expired_enabled',
    severity: 'error',
    filters: [
      ...distributable(false),
      ...IMPORTED,
      // Licence ended 14+ days ago (on or before today − 14). Open-ended
      // periods blocked for other reasons have no `ended_at` and drop out.
      {
        field: 'ended_at',
        operator: 'lt',
        value: today.subtract(13, 'day').format(DAY),
      },
      // The licence belongs to the format, so an enabled format without one
      // is the problem — not the publication as a whole.
      {
        field: 'user_catalog_product_mod_status',
        operator: 'eq',
        value: 'enabled',
      },
    ],
  },
  {
    key: 'not_imported',
    severity: 'warning',
    // Upcoming periods count too, so `is_distributable` (open period today)
    // can't gate this. The API has no OR, so only the contract part of the
    // gate is kept; unsupported or scorecard-blocked products slip through.
    filters: [
      { field: 'status', operator: 'in', value: ['available', 'upcoming'] },
      { field: 'publisher_has_active_contract', operator: 'eq', value: true },
      { field: 'user_catalog_publication_id', operator: 'null', value: true },
    ],
  },
  {
    key: 'licensed_disabled',
    severity: 'warning',
    filters: [
      ...distributable(true),
      ...IMPORTED,
      // The licence belongs to the format, so its status is what matters here.
      {
        field: 'user_catalog_product_mod_status',
        // Test is as invisible to readers as disabled.
        operator: 'in',
        value: ['disabled', 'test'],
      },
    ],
  },
  {
    key: 'no_contract',
    severity: 'warning',
    filters: [
      { field: 'status', operator: 'eq', value: 'available' },
      // Running periods whose publisher has no signed contract: nothing to
      // bill against.
      { field: 'publisher_has_active_contract', operator: 'eq', value: false },
    ],
  },
  {
    key: 'ending_3d',
    severity: 'warning',
    filters: dateWindow('ending', '3d', today),
  },
  {
    key: 'active_today',
    filters: [{ field: 'status', operator: 'eq', value: 'available' }],
  },
  {
    key: 'starting_today',
    filters: dateWindow('starting', 'today', today),
  },
];

const status = (value: string): CrudFilters => [
  { field: 'status', operator: 'eq', value },
];

/**
 * Options of the "Widok" select: status views, one entry per date
 * dimension (resolved with `selection`), then the alerts.
 */
export const viewQueries = (
  today = dayjs(),
  selection: DateSelection = DEFAULT_DATE_SELECTION
): SummaryQuery[] => [
  { key: 'all', filters: [] },
  // "Active" is the period alone: running today, whether or not it has been
  // imported to the user catalog. Reader visibility is what the alerts cover.
  { key: 'available', filters: status('available') },
  { key: 'upcoming', filters: status('upcoming') },
  { key: 'expired', filters: status('expired') },
  ...DATE_DIMENSIONS.map((dimension) => ({
    key: `${dimension}_in` as DateViewKey,
    filters: dateFilters(dimension, selection, today),
  })),
  ...alertQueries(today),
];

export const alertQueries = (today?: dayjs.Dayjs) =>
  summaryQueries(today).filter((q) => q.severity != null);

export const statQueries = (today?: dayjs.Dayjs) =>
  summaryQueries(today).filter(
    (q) => q.key === 'active_today' || q.key === 'starting_today'
  );

const isDay = (value: string | null): value is string =>
  value != null && dayjs(value, DAY, true).isValid();

/** `?window=` / `?from=`+`?to=` — the date view's window as URL state. */
export const parseDateSelection = (params: URLSearchParams): DateSelection => {
  const from = params.get('from');
  const to = params.get('to');
  // A hand-edited URL must not reach the pickers or the API as garbage.
  if (isDay(from) && isDay(to) && from <= to)
    return { kind: 'range', from, to };
  const window = params.get('window');
  return (WINDOWS as string[]).includes(window ?? '')
    ? { kind: 'window', window: window as Window }
    : DEFAULT_DATE_SELECTION;
};

/** Writes `selection` into `params`, clearing the keys it does not use. */
export const writeDateSelection = (
  params: URLSearchParams,
  selection: DateSelection
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete('window');
  next.delete('from');
  next.delete('to');
  if (selection.kind === 'range') {
    next.set('from', selection.from);
    next.set('to', selection.to);
  } else if (selection.window !== DEFAULT_DATE_SELECTION.window) {
    next.set('window', selection.window);
  }
  return next;
};
